import { useState, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Stack,
  Typography,
  Card,
  Skeleton,
} from "@mui/material";
import { Icon } from "@iconify/react";

// ** Components
import UserCard from "@/views/admin/users/UserCard.tsx";
import AppModal from "@/components/modal/AppModal.tsx";
import { UserForm } from "@/views/admin/users/UserForm.tsx";
import ToggleUserModal from "@/views/admin/users/ToggleUserModal.tsx";

// ** Hooks
import {
  useGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
} from "@/api/usersApi.ts";

// ** Utils
import { mutationFeedback } from "@/services/utils.tsx";

// ** Types
import type { UserCreateFormType, UserEditFormType } from "@/schemas/user.ts";
import { useAppSelector } from "@/store/hooks.ts";

// ** Types
import type { components } from "@/types/api/generated-api-schema.ts";

type User = components["schemas"]["User.jsonld-user.read_read"];

export const Route = createFileRoute("/admin/users")({
  component: UsersPage,
});

function UsersPage() {
  const { t } = useTranslation();

  // Get current user from auth state
  const currentUser = useAppSelector((state) => state.auth.user);

  // Modal states
  const [formOpen, setFormOpen] = useState(false);
  const [toggleOpen, setToggleOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // API
  const { data, isLoading, error } = useGetUsersQuery({});
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const isSubmitting = isCreating || isUpdating;

  const users = useMemo(() => data?.member ?? [], [data?.member]);

  // Count active admins
  // Note: roles type is incorrectly generated as null[] - cast to string[] as workaround
  const activeAdminCount = useMemo(() => {
    return users.filter(
      (user) => user.enabled && (user.roles as unknown as string[])?.includes("ROLE_ADMIN")
    ).length;
  }, [users]);

  const handleAdd = () => {
    setSelectedUser(null);
    setFormOpen(true);
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setFormOpen(true);
  };

  const handleToggleEnabled = (user: User) => {
    setSelectedUser(user);
    setToggleOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedUser(null);
  };

  const handleUserSubmit = async (data: UserCreateFormType | UserEditFormType) => {
    let result;

    if (selectedUser?.uuid) {
      // For edit, only include password if provided
      const updateData: Record<string, unknown> = {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
      };
      if (data.plainPassword) {
        updateData.plainPassword = data.plainPassword;
      }
      result = await updateUser({ uuid: selectedUser.uuid, data: updateData });
    } else {
      // For create - ROLE_ADMIN is assigned automatically by backend processor
      result = await createUser({
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        plainPassword: data.plainPassword!,
        enabled: true,
      });
    }

    mutationFeedback(result, {
      success: selectedUser
        ? t("admin.users.messages.updated")
        : t("admin.users.messages.created"),
      error: selectedUser
        ? t("admin.users.messages.updateError")
        : t("admin.users.messages.createError"),
    });

    if (!("error" in result)) {
      handleFormClose();
    }
  };

  const handleToggleClose = () => {
    setToggleOpen(false);
    setSelectedUser(null);
  };

  // Check if a user can be toggled (disabled/enabled)
  const canToggleUser = (user: User): { canToggle: boolean; reason?: string } => {
    // Cannot disable yourself
    if (user.uuid === currentUser?.uuid) {
      return {
        canToggle: false,
        reason: t("admin.users.errors.cannotDisableSelf"),
      };
    }

    // Cannot disable the last active admin
    if (user.enabled && (user.roles as unknown as string[])?.includes("ROLE_ADMIN") && activeAdminCount <= 1) {
      return {
        canToggle: false,
        reason: t("admin.users.errors.cannotDisableLastAdmin"),
      };
    }

    return { canToggle: true };
  };

  if (error) {
    return (
      <Box>
        <Typography color="error">{t("admin.common.loadingError")}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight={600} color="text.primary">
          {t("admin.users.title")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Icon icon="tabler:plus" />}
          onClick={handleAdd}
        >
          {t("admin.users.addUser")}
        </Button>
      </Stack>

      {/* Count */}
      {!isLoading && users.length > 0 && (
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t("admin.users.count", { count: users.length })}
        </Typography>
      )}

      {/* Content */}
      {isLoading ? (
        <Stack spacing={2}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={88} sx={{ borderRadius: 1 }} />
          ))}
        </Stack>
      ) : users.length === 0 ? (
        <Card sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary" mb={2}>
            {t("admin.users.noUsers")}
          </Typography>
          <Button variant="contained" onClick={handleAdd}>
            {t("admin.users.addUser")}
          </Button>
        </Card>
      ) : (
        <Stack spacing={2}>
          {users.map((user) => {
            const { canToggle, reason } = canToggleUser(user);
            return (
              <UserCard
                key={user.uuid}
                user={user}
                onEdit={() => handleEdit(user)}
                onToggleEnabled={() => handleToggleEnabled(user)}
                canToggle={canToggle}
                disableReason={reason}
              />
            );
          })}
        </Stack>
      )}

      {/* Form Modal - key forces remount when switching between create/edit */}
      <AppModal
        key={selectedUser?.uuid ?? "create"}
        open={formOpen}
        onClose={handleFormClose}
        title={selectedUser ? t("admin.users.editUser") : t("admin.users.addUser")}
        sx={{ width: 500, maxHeight: "90vh", overflow: "auto" }}
      >
        <UserForm
          user={selectedUser}
          onSubmit={handleUserSubmit}
          onCancel={handleFormClose}
          isSubmitting={isSubmitting}
        />
      </AppModal>

      {/* Toggle Modal */}
      <ToggleUserModal
        open={toggleOpen}
        onClose={handleToggleClose}
        user={selectedUser}
      />
    </Box>
  );
}
